/**
 * Friendly display names for layers whose GeoServer title is still the raw
 * technical name. Purely presentational — the qualified `name` is untouched and
 * still drives every WMS request. A GeoServer title can't carry rich formatting,
 * so the italic "sub" suffix (rendered smaller, see `.layer-name__sub`) lives here.
 *
 * A layer carries both a WMS Name (technical) and a Title (what the panel shows
 * today). The technical string may live in either, so a key is matched against
 * BOTH — unqualified name, full name, and title — stripping any "workspace:"
 * qualifier first (so `mining:pub_tbl01_claims_canadian` maps like the bare
 * `pub_tbl01_claims_canadian`).
 */

export interface DisplayName {
  /** Main label. */
  label: string
  /** Optional suffix, shown italic + smaller (e.g. "(day old categorized)"). */
  sub?: string
}

/**
 * Overrides, for the few layers whose advertised strings are *both* technical or
 * whose label needs information GeoServer cannot carry (the italic `sub`).
 *
 * Deliberately short. Production advertises readable Names — `Mining assets
 * (CAD)`, `Provincial geology (USA)` — and `displayName()` now shows them, so an
 * entry here is only worth adding when the advertised strings are not enough. An
 * entry that merely restates the Name is one more thing to keep in step with
 * GeoServer.
 */
const DISPLAY_NAMES: Record<string, DisplayName> = {
  // A layer *group*: both its Name (`canada_claims_subscriber`) and its Title
  // are identifiers, so nothing readable is advertised for it at all.
  group_pub_tbl01_claims_canadian: { label: 'Canadian claims', sub: '(day old categorized)' },
  canada_claims_subscriber: { label: 'Canadian claims', sub: '(day old categorized)' },
  // "same owner" is the point of these two and appears in neither string. Split
  // by country in GeoServer on 2026-07-31 — there is no plain `Property outline
  // colored` any more, so keying on it (or on the old `_colored` table alias)
  // would match nothing. The country marker has to survive into the label, or
  // the pair reads as the same layer twice.
  'Property outline colored (CAD)': { label: 'Property outline', sub: '(cad, coloured by owner)' },
  'Property outline colored (USA)': { label: 'Property outline', sub: '(usa, coloured by owner)' },
  'Property outline (coloured by owner)': { label: 'Property outline', sub: '(coloured by owner)' },
  // GeoServer advertises this one with a double space — see `Property outline
  // (trial)` in the inventory. Kept verbatim as the key, tidied in the label.
  'Property outline  (trial)': { label: 'Property outline', sub: '(trial)' },
  'Property outline (trial)': { label: 'Property outline', sub: '(trial)' },
  // "Map areas" names the table. Under an "Areas" heading it would also read as
  // a tautology. Approved wording: Mining district.
  'Map areas (CAD)': { label: 'Mining district' },
  'Map areas (USA)': { label: 'Mining district', sub: '(usa)' },
  tbl40_map_areas: { label: 'Mining district' },
  // "Provincial" reads as Canada-only to a US customer even on the (USA)
  // variant. Approved wording, Pierre 2026-09-15: State geology.
  'Provincial geology (CAD)': { label: 'State geology', sub: '(cad)' },
  'Provincial geology (USA)': { label: 'State geology', sub: '(usa)' },
}

const DISPLAY_INDEX: Record<string, DisplayName> = Object.fromEntries(
  Object.entries(DISPLAY_NAMES).map(([key, value]) => [lookupKey(key), value]),
)

/* ── Taxonomy ──────────────────────────────────────────────────────────
 *
 * Five groups in a fixed order, so the panel reads the same whatever order
 * GetCapabilities happens to advertise. Since 2026-09-09 the map stacks in the
 * same order, upside down — see `stackRank()`: what is listed first is drawn
 * last, on top, so **Assets first is not cosmetic** and Geology last is not
 * either. This list still never decides what the map *requests*.
 *
 * Keyed on the GeoServer `<Name>`, never on the table. Two pairs —
 * `Mining assets (CAD)`/`(USA)` and `Provincial geology (CAD)`/`(USA)` — are one
 * table each, split by a CQL filter configured in GeoServer and invisible from
 * the database. A registry keyed on the table collapses each pair into one
 * entry. Measured 2026-07-29; see the inventory note in the API repo.
 */

export const GROUP_ORDER = ['Assets', 'Areas', 'Claims', 'Property', 'Geology'] as const

export type LayerGroup = (typeof GROUP_ORDER)[number]

/** Which group a layer belongs to, and where it sits inside it. */
const GROUPS: Record<string, { group: LayerGroup; order: number }> = {
  // 01 — Assets. First, so its point symbols are not buried under the areas.
  'Mining assets (CAD)': { group: 'Assets', order: 1 },
  'Mining assets (USA)': { group: 'Assets', order: 2 },
  'Mining assets (Trial)': { group: 'Assets', order: 3 },
  tbl08_assets_vm: { group: 'Assets', order: 1 }, // the mock's assets layer

  // 02 — Areas.
  'Map areas (CAD)': { group: 'Areas', order: 1 },
  'Map areas (USA)': { group: 'Areas', order: 2 },
  'Protected areas (CAD)': { group: 'Areas', order: 3 },
  'Protected areas (USA)': { group: 'Areas', order: 4 },

  // 03 — Claims.
  'Canadian claims': { group: 'Claims', order: 1 },
  canadian_claims: { group: 'Claims', order: 1 }, // the mock's claims layer
  'Canadian claims (trial)': { group: 'Claims', order: 2 },
  // A layer *group* rather than a layer, and absent from the PRD taxonomy.
  // Pierre ruled on 2026-07-29: it belongs in Claims. Without a ruling it would
  // have fallen to the ungrouped tail, where a customer finds one stray entry
  // below five tidy sections.
  canada_claims_subscriber: { group: 'Claims', order: 3 },
  'USA claims': { group: 'Claims', order: 4 },

  // 04 — Property.
  'Property outline': { group: 'Property', order: 1 },
  property_outline: { group: 'Property', order: 1 }, // the mock's property layer
  'Property outline colored (CAD)': { group: 'Property', order: 2 },
  'Property outline colored (USA)': { group: 'Property', order: 3 },
  'Property outline (coloured by owner)': { group: 'Property', order: 4 },
  'Property outline  (trial)': { group: 'Property', order: 5 },
  'Property outline (trial)': { group: 'Property', order: 5 },

  // 05 — Geology.
  'Canadian Regional geology': { group: 'Geology', order: 1 },
  'Provincial geology (CAD)': { group: 'Geology', order: 2 },
  'Provincial geology (USA)': { group: 'Geology', order: 3 },
}

/**
 * A trailing GeoServer variant marker — ` (CAD)`, ` (USA)`, ` (trial)`. Layers
 * are split by adding one, so everything before it is the family name.
 */
const VARIANT_SUFFIX = /\s*\([^()]*\)\s*$/

/** `Protected areas (CAD)` → `Protected areas`. */
function family(name: string): string {
  return name.replace(VARIANT_SUFFIX, '').trim()
}

/**
 * The registry above, re-indexed by family, so a variant nobody has recorded
 * yet still lands under its family's heading.
 *
 * **This exists because splitting a layer is a routine GeoServer edit that
 * touches no repository.** On 2026-07-31 `Protected areas` and `Property
 * outline colored` each became a `(CAD)`/`(USA)` pair, to give the
 * `viewer-canada` plan a Canada-only tier. Three layers the panel knew by exact
 * string stopped existing that morning and two new ones appeared, and nothing
 * in either repo changed. Keyed on exact names alone, five served layers sat in
 * the untidy tail while every suite stayed green.
 *
 * A family keeps the lowest order of its members, so a new variant sorts at the
 * head of its family rather than jumping the group.
 */
const FAMILIES: Record<string, { group: LayerGroup; order: number }> = (() => {
  const out: Record<string, { group: LayerGroup; order: number }> = {}
  for (const [key, hit] of Object.entries(GROUPS)) {
    const f = lookupKey(family(key))
    if (!f) continue
    const seen = out[f]
    if (!seen || hit.order < seen.order) out[f] = hit
  }
  return out
})()

const GROUP_INDEX: Record<string, { group: LayerGroup; order: number }> = Object.fromEntries(
  Object.entries(GROUPS).map(([key, value]) => [lookupKey(key), value]),
)

/**
 * The group a layer belongs to, or `null` when the registry has never heard of
 * it.
 *
 * A null is deliberate and must stay visible: an unmapped layer renders after
 * the known groups rather than disappearing. A layer added to GeoServer and
 * forgotten here should look untidy, not absent — a customer paying for it
 * would otherwise never know it arrived.
 */
export function layerGroup(
  name: string,
  fallbackTitle = '',
): { group: LayerGroup | null; order: number } {
  for (const key of [unqualified(name), name, unqualified(fallbackTitle), fallbackTitle]) {
    const hit = key ? GROUP_INDEX[lookupKey(key)] : undefined
    if (hit) return hit
  }
  // Only reached for a layer no entry names. An explicit entry always wins, so
  // this never overrides a deliberate placement — it only spares an unrecorded
  // variant from the tail.
  for (const key of [unqualified(name), unqualified(fallbackTitle)]) {
    const hit = key ? FAMILIES[lookupKey(family(key))] : undefined
    if (hit) return hit
  }
  return { group: null, order: 0 }
}

/**
 * Sort layers into the fixed group order, each group internally ordered.
 *
 * Anything the registry does not know is returned under `null`, last. Generic
 * over the caller's layer type so this module keeps knowing nothing about
 * OpenLayers.
 */
export function groupLayers<T>(
  items: T[],
  identify: (item: T) => { name: string; title: string },
): { group: LayerGroup | null; items: T[] }[] {
  const buckets = new Map<LayerGroup | null, { item: T; order: number; label: string }[]>()

  for (const item of items) {
    const { name, title } = identify(item)
    const { group, order } = layerGroup(name, title)
    const label = displayNameText(name, title)
    buckets.set(group, [...(buckets.get(group) ?? []), { item, order, label }])
  }

  const sections: { group: LayerGroup | null; items: T[] }[] = []
  for (const group of GROUP_ORDER) {
    const bucket = buckets.get(group)
    if (!bucket?.length) continue // an empty group is not a heading
    sections.push({
      group,
      // Two variants resolved through the same family share an order. Breaking
      // the tie on the label rather than leaving it to sort stability keeps the
      // panel identical whatever order GetCapabilities advertises — which is
      // the whole point of ordering here at all.
      items: [...bucket]
        .sort((a, b) => a.order - b.order || a.label.localeCompare(b.label))
        .map((x) => x.item),
    })
  }
  const unmapped = buckets.get(null)
  if (unmapped?.length) sections.push({ group: null, items: unmapped.map((x) => x.item) })
  return sections
}

/** Strip a leading `workspace:` qualifier, if present. */
export function unqualified(name: string): string {
  const i = name.lastIndexOf(':')
  return i === -1 ? name : name.slice(i + 1)
}

/**
 * Registry matching is presentational and must tolerate harmless GeoServer
 * title edits: case changes, repeated spaces, and country markers with different
 * capitalization. It never mutates the WMS Name used in requests.
 */
export function lookupKey(s: string): string {
  return s.replace(/\s+/g, ' ').trim().toLowerCase()
}

/**
 * Whether a string is a database identifier rather than something to show a
 * customer: lowercase letters, digits and underscores, nothing else.
 *
 * `pub_tbl06_property_na_trial` yes; `Property outline  (trial)` no.
 */
function looksTechnical(s: string): boolean {
  return /^[a-z0-9_]+$/.test(s)
}

/**
 * Friendly display name for a layer.
 *
 * With no override, it shows whichever of the two advertised strings is not a
 * database identifier. **This is what the fallback used to get wrong**: it
 * always returned the Title, and on the production server the Title is the
 * table — so ten of the fourteen layers were labelled `tbl15_can_geology`,
 * `tbl_sync_asset` and the like, two of them twice over because a pair of layers
 * can share one table and be split by a CQL filter GeoServer never advertises.
 *
 * The two servers disagree about which string is which — the mock puts the
 * identifier in the Name and the words in the Title, production does the
 * opposite — and the panel cannot know which one it is talking to. So it does
 * not ask: it takes whichever string reads as words. That is also why this test
 * runs against a recorded production fixture (`scripts/layer-names-test.mjs`)
 * and not only against the mock, which was green throughout.
 */
export function displayName(name: string, fallbackTitle: string): DisplayName {
  for (const key of [unqualified(name), name, unqualified(fallbackTitle), fallbackTitle]) {
    const hit = key ? DISPLAY_INDEX[lookupKey(key)] : undefined
    if (hit) return hit
  }
  const bare = unqualified(name)
  if (bare && !looksTechnical(bare)) return readableDisplay(bare)
  if (fallbackTitle && !looksTechnical(fallbackTitle)) return readableDisplay(fallbackTitle)
  return { label: fallbackTitle || bare }
}

function readableDisplay(s: string): DisplayName {
  const match = s.match(/^(.*?)\s*(\([^()]*\))\s*$/)
  if (!match) return { label: s }
  return {
    label: match[1].trim(),
    sub: match[2].toLowerCase(),
  }
}

/** Flat plain-text form (`label` + optional `sub`), for aria-labels/alt text. */
export function displayNameText(name: string, fallbackTitle: string): string {
  const d = displayName(name, fallbackTitle)
  return d.sub ? `${d.label} ${d.sub}` : d.label
}

/**
 * Whether a layer draws single points rather than areas.
 *
 * A point is one small symbol, an area is a whole surface, and whichever is
 * drawn last wins: Assets under Property means the icons vanish under the
 * parcels. The registry now puts the assets on top by name; this is the safety
 * net for a point layer it has never heard of (see `stackRank()`). Matched on
 * the same substring the label rules use, against name and title alike.
 */
export function isPointLayer(name: string, title = ''): boolean {
  return `${unqualified(name)} ${title}`.toLowerCase().includes('asset')
}

/* ── Map stacking ──────────────────────────────────────────────────────
 *
 * The map mirrors the panel: what is listed first is drawn last, on top — the
 * convention of every desktop GIS, and what Pierre expected when he reported
 * geology burying claims, districts and protected areas (2026-09-09). Geology
 * is the base: an opaque fill that must never mask anything drawn over it.
 *
 * Before this, every area layer shared one z-index and the order of the
 * capabilities document decided who buried whom — an order nobody controls.
 */

/**
 * Bottom to top. A layer the registry does not know sits just above the base:
 * the panel lists it last so it looks untidy, but under an opaque geology it
 * would look absent — and a stranger must not cover the named groups either.
 */
const STACK: readonly (LayerGroup | null)[] = (() => {
  const [base, ...rest] = [...GROUP_ORDER].reverse()
  return [base, null, ...rest]
})()

/**
 * Where a layer sits in the stack: 0 is the bottom, `STACK.length - 1` the top.
 * Group ranks only — the row order inside a group is honoured by `stackOrder()`.
 *
 * `isPointLayer()` stays as the safety net it always was: an unmapped point
 * layer still stacks with the assets, because a symbol under a fill is gone.
 */
export function stackRank(name: string, title = ''): number {
  const { group } = layerGroup(name, title)
  return STACK.indexOf(group ?? (isPointLayer(name, title) ? 'Assets' : null))
}

/**
 * The layers in drawing order, bottom first, for `map.addLayer()`.
 *
 * Equal ranks share a z-index, and OpenLayers breaks that tie on insertion
 * order — so adding the layers in this order honours the panel's row order
 * inside a group too: listed first, drawn last. Strangers, which the panel
 * keeps last, move to their rank; the stable sort leaves every tie as it was.
 */
export function stackOrder<T>(
  items: T[],
  identify: (item: T) => { name: string; title: string },
): T[] {
  const bottomUp = groupLayers(items, identify).flatMap((s) => s.items).reverse()
  return bottomUp
    .map((item, i) => {
      const { name, title } = identify(item)
      return { item, i, rank: stackRank(name, title) }
    })
    .sort((a, b) => a.rank - b.rank || a.i - b.i)
    .map((x) => x.item)
}
